import React from 'react';
import type { ChartComponentProps } from './chartConfig';
import { BarChart } from './BarChart';
import { LineChart } from './LineChart';
import { DualAxisLineChart } from './DualAxisLineChart';
import { PieChart } from './PieChart';
import { SunburstChart } from './SunburstChart';
import { ScatterChart } from './ScatterChart';
import { RadarChart } from './RadarChart';
import { HeatmapChart } from './HeatmapChart';
import { TreemapChart } from './TreemapChart';
import { FunnelChart } from './FunnelChart';
import { GaugeChart } from './GaugeChart';
import { BulletChart } from './BulletChart';
import { KPISparklineChart } from './KPISparklineChart';
import { CandlestickChart } from './CandlestickChart';
import { WaterfallChart } from './WaterfallChart';
import { BoxplotChart } from './BoxplotChart';
import { ParallelChart } from './ParallelChart';
import { MatrixChart } from './MatrixChart';
import { chartFromTemplate } from './chartTemplate';

const chartMap: Record<string, React.FC<ChartComponentProps>> = {
  // Line family
  line: LineChart,
  area: LineChart,
  dual_axis_line: DualAxisLineChart,
  // Combo: bar series on left axis (USD), line series on right axis (%).
  // Same renderer as dual_axis_line — it reads chart.series[] to route
  // each measure to the right axis with the right marker type.
  dual_axis_bar_line: DualAxisLineChart,

  // Bar family
  bar: BarChart,
  horizontal_bar: BarChart,
  multi_column_bar: BarChart,
  stacked_bar: BarChart,
  diverging_bar: BarChart,
  histogram: BarChart,

  // Waterfall
  waterfall: WaterfallChart,

  // Pie family
  pie: PieChart,
  donut: PieChart,
  sunburst: SunburstChart,

  // Matrix / heatmap family
  matrix: MatrixChart,
  heatmap: HeatmapChart,
  sensitivity_matrix: HeatmapChart,
  correlation_matrix: HeatmapChart,

  // Hierarchical
  treemap: TreemapChart,
  funnel: FunnelChart,

  // Scatter family
  scatter: ScatterChart,
  bubble: ScatterChart,

  // Gauge / KPI family — gauge chart commented out for now
  // gauge: GaugeChart,
  bullet: BulletChart,
  kpi_sparkline: KPISparklineChart,

  // Radar & parallel
  radar: RadarChart,
  parallel_coordinates: ParallelChart,

  // Statistical
  boxplot: BoxplotChart,
  box_plot: BoxplotChart,

  // Financial
  candlestick: CandlestickChart,
  gauge: GaugeChart,
};

export const ChartRenderer: React.FC<ChartComponentProps> = (props) => {
  const chart = chartFromTemplate(props.chart);
  if ((!chart.data || chart.data.length === 0) && (!chart.cells || chart.cells.length === 0)) return null;
  const Component = chartMap[chart.type] || BarChart;
  return <Component {...props} chart={chart} />;
};
